// /admin/api-tokens — personal access tokens (T127).
//
// Layout:
//   - Header with page title.
//   - "Create token" button → inline form that expands above the list.
//   - Token list table: name, scopes, last-used, created-at, revoke action.
//   - Copy-once banner: after create, a dismissible banner shows the plaintext
//     token with a "Copy" button. Once dismissed, the plaintext is gone.

import { FormEvent, useState } from 'react';
import useSWR from 'swr';
import {
  Check,
  ClipboardCopy,
  KeyRound,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  ADMIN_API_TOKENS_KEY,
  PAT_SCOPES,
  PersonalAccessToken,
  adminApiTokensApi,
  fetcher,
} from '../lib/api';
import { PageHeader, Table, TBody, TD, TH, THead, TR } from '../components/ui';

export function AdminApiTokens() {
  const { data, error, isLoading, mutate } =
    useSWR<PersonalAccessToken[]>(ADMIN_API_TOKENS_KEY, fetcher, {
      revalidateOnFocus: false,
    });

  const [showCreate, setShowCreate] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);

  const handleCreated = (token: PersonalAccessToken) => {
    setShowCreate(false);
    if (token.token) {
      setNewToken(token.token);
    }
    mutate();
  };

  const handleRevoke = async (id: string) => {
    await adminApiTokensApi.revoke(id);
    mutate();
  };

  let tokenListContent;
  if (isLoading) {
    tokenListContent = (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading tokens…</span>
      </div>
    );
  } else if (error) {
    tokenListContent = <ErrorBanner />;
  } else if (!data || data.length === 0) {
    tokenListContent = (
      <div className="orb-card p-8 text-center text-fg-muted text-sm">
        No tokens yet. Create one to get started.
      </div>
    );
  } else {
    tokenListContent = <TokenTable tokens={data} onRevoke={handleRevoke} />;
  }

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<KeyRound />}
        title="API Tokens"
        description={
          <>
            Personal access tokens authenticate API calls on your behalf. Each
            token has a fixed scope set; the plaintext is shown once at creation.{' '}
            <a
              href="/api/v1/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:underline"
            >
              View interactive API docs →
            </a>
          </>
        }
        actions={
          showCreate ? null : (
            <button
              onClick={() => setShowCreate(true)}
              className={primaryBtnCls}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Create token
            </button>
          )
        }
      />

      {/* Copy-once banner */}
      {newToken && (
        <CopyOnceBanner token={newToken} onDismiss={() => setNewToken(null)} />
      )}

      {/* Create form */}
      {showCreate && (
        <CreateTokenForm
          onCreated={handleCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Token list */}
      {tokenListContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy-once banner
// ---------------------------------------------------------------------------

function CopyOnceBanner({ token, onDismiss }: { readonly token: string; readonly onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      role="alert"
      className="orb-card mb-6 p-5 border-success/40 bg-success/5 space-y-3"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-fg">Token created — copy it now</p>
          <p className="text-xs text-fg-muted mt-0.5">
            This value will not be shown again. Store it in a secrets manager.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="p-1 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg transition-colors"
          title="Dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <code className="flex-1 font-mono text-xs bg-bg-inset border border-border rounded px-3 py-2 truncate text-fg select-all">
          {token}
        </code>
        <button
          onClick={handleCopy}
          className={`${primaryBtnCls} shrink-0`}
        >
          {copied ? (
            <><Check className="h-3.5 w-3.5" aria-hidden="true" /> Copied</>
          ) : (
            <><ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" /> Copy</>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create token form
// ---------------------------------------------------------------------------

interface CreateFormProps {
  readonly onCreated: (token: PersonalAccessToken) => void;
  readonly onCancel: () => void;
}

function CreateTokenForm({ onCreated, onCancel }: CreateFormProps) {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleScope = (scope: string) => {
    const next = new Set(selectedScopes);
    if (next.has(scope)) next.delete(scope);
    else next.add(scope);
    setSelectedScopes(next);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Token name is required.'); return; }
    if (selectedScopes.size === 0) { setErr('Select at least one scope.'); return; }
    setSaving(true);
    try {
      const created = await adminApiTokensApi.create({
        name: name.trim(),
        scopes: Array.from(selectedScopes),
      });
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create token.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="orb-card mb-6 p-5 space-y-4">
      <p className="text-sm font-semibold text-fg">New personal access token</p>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="pat-name" className="orb-section-title text-[11px]">
          Token name
        </label>
        <input
          id="pat-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
          placeholder="e.g. laptop, ci-runner"
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <p className="orb-section-title text-[11px]">Scopes</p>
        <p className="text-[11px] text-fg-muted">
          Select the API areas this token may access. Principle of least privilege applies.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PAT_SCOPES.map((scope) => (
            <label
              key={scope}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={selectedScopes.has(scope)}
                onChange={() => toggleScope(scope)}
                className="accent-accent h-4 w-4 shrink-0"
              />
              <span className="font-mono text-xs text-fg group-hover:text-accent transition-colors">
                {scope}
              </span>
            </label>
          ))}
        </div>
      </div>

      {err && <p role="alert" className="text-xs text-danger">{err}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Creating…</>
          ) : (
            'Create token'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-fg-muted hover:text-fg transition-colors px-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Token table
// ---------------------------------------------------------------------------

interface TokenTableProps {
  readonly tokens: PersonalAccessToken[];
  readonly onRevoke: (id: string) => void | Promise<void>;
}

function TokenTable({ tokens, onRevoke }: TokenTableProps) {
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this token? Any applications using it will lose access immediately.')) return;
    setRevoking(id);
    try {
      await onRevoke(id);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Table>
      <THead>
        <TR>
          <TH>Name</TH>
          <TH>Scopes</TH>
          <TH>Last used</TH>
          <TH>Created</TH>
          <TH srOnly>Actions</TH>
        </TR>
      </THead>
      <TBody>
          {tokens.map((t) => (
            <TR key={t.id} muted={t.revoked}>
              <TD primary>
                {t.name}
                {t.revoked && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-danger/70">
                    revoked
                  </span>
                )}
              </TD>
              <TD>
                <div className="flex flex-wrap gap-1">
                  {(t.scopes ?? []).map((s) => (
                    <span
                      key={s}
                      className="inline-block font-mono text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </TD>
              <TD mono>
                {t.last_used ? fmtDate(t.last_used) : <span className="italic">never</span>}
              </TD>
              <TD mono>
                {fmtDate(t.created_at)}
              </TD>
              <TD align="right">
                {!t.revoked && (
                  <button
                    onClick={() => handleRevoke(t.id)}
                    disabled={revoking === t.id}
                    className="p-1.5 rounded text-fg-muted hover:text-danger hover:bg-danger/10 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30"
                    title="Revoke token"
                  >
                    {revoking === t.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </button>
                )}
              </TD>
            </TR>
          ))}
      </TBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function ErrorBanner() {
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">Could not load API tokens.</p>
      <p className="text-xs text-fg-muted mt-2">
        Only org admins can manage API tokens. Verify your role or check the
        API server logs.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers + styles
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
